/* global Word */

import React, { useEffect, useState } from "react";
import { CoObjectItem } from "../interfaces/CoObject";
import HeaderPanel from "../layout/HeaderPanel";
import { List } from "@fluentui/react";
import { CoObject, CoObjectTypeEnum, VersionedDocumentObject } from "../../api/models";
import { getServerVersionedDocumentObjects, syncVersionedDocumentObjects } from "../../api/object/CoObjectGeneral";
import FooterPanel from "../layout/FooterPanel";
import NotificationManager from "../../common/notification/NotificationManager";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import {
  getDocumentAdvancedCustomXml,
  mapAdvancedDocumentToDocPartMetadata,
} from "../../utils/helpers/DocumentAdvancedCustomXmlHelper";
import { DocumentPartMetadata } from "../../api/models/document-part-metadata";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import LoadingManager from "../../common/loading/LoadingManager";
import { SYNCH_RESTORE } from "../../utils/constants/navigation";
import { SyncVersionedDocumentObjectRequest } from "../../api/models/sync-versioned-document-object-request";
import { getContentControlsWithFirstLevelChild } from "../../office/ContentControl";
import { moveCursorToContentControlId } from "../../office/WordDocumentPart";
import ContentControl = Word.ContentControl;

export interface SyncRestoreProps {
  documentCoObjects: CoObjectItem[];
  documentId: string;
}

export interface SyncRestoreObjectInfo {
  name: string;
  type: string;
  id?: string;
  contentControlId?: string;
}

export interface SynchRestoreState {
  unSyncObjects: {
    atDifferentVersion: SyncRestoreObjectInfo[];
    notPresentInDoc: SyncRestoreObjectInfo[];
    notActiveOnServer: SyncRestoreObjectInfo[];
    notTagetik: SyncRestoreObjectInfo[];
    tot: number;
  };
}

const getDocumentMetadataFromCustomXML = async (): Promise<DocumentPartMetadata> => {
  const editDocumentCustomXML = await getDocumentAdvancedCustomXml();
  return editDocumentCustomXML
    ? mapAdvancedDocumentToDocPartMetadata({
        documentAdvancedCustomXML: editDocumentCustomXML,
      })
    : null;
};

const parseVersionedObjectDiffToState = (
  serverVersionedDocumentObjects: VersionedDocumentObject,
  documentCoObjects: CoObjectItem[],
  contentControlsWithFirstLevelChild: ContentControl[],
  customXMLMetadata: DocumentPartMetadata
): SynchRestoreState => {
  const serverCoObjects = serverVersionedDocumentObjects.coObjects;

  const docIdMap: Map<string, CoObjectItem> = new Map(documentCoObjects.map((obj) => [obj.id, obj]));
  const docCcIdMap: Map<string, CoObjectItem> = new Map(
    documentCoObjects.map((obj) => [`${obj.contentControlId}`, obj])
  );
  const serverIdMap: Map<string, CoObject> = new Map();
  const serverCcIdMap: Map<string, CoObject> = new Map();

  const atDifferentVersion: SyncRestoreObjectInfo[] = [];
  const notPresent: SyncRestoreObjectInfo[] = [];

  serverCoObjects.forEach((ro) => {
    if (!docIdMap.has(ro.id)) {
      if (!docCcIdMap.has(ro.contentControlId)) {
        notPresent.push({
          id: ro.id,
          name: ro.name,
          type: CoObjectTypeEnum[ro.type],
          contentControlId: contentControlsWithFirstLevelChild.some((cc) => cc.id.toString() === ro.contentControlId)
            ? ro.contentControlId
            : undefined,
        });
      } else {
        const docObject = docCcIdMap.get(ro.contentControlId);
        atDifferentVersion.push({
          id: docObject.id,
          name: docObject.info.objectName,
          type: docObject.info.objectType,
          contentControlId: ro.contentControlId,
        });
      }
    }
    serverIdMap.set(ro.id, ro);
    serverCcIdMap.set(ro.contentControlId, ro);
  });
  const notActiveOnServer: SyncRestoreObjectInfo[] = documentCoObjects
    .filter(
      (docObj) => !serverIdMap.has(docObj.id) && atDifferentVersion.findIndex((item) => item.id === docObj.id) == -1
    )
    .map((docObj) => {
      return {
        id: docObj.id,
        name: docObj.info.objectName,
        type: docObj.info.objectType,
        contentControlId: docObj.contentControlId,
      };
    });

  const metadataMessageItem = {
    id: serverVersionedDocumentObjects.documentPartId,
    name: formatMessage("cof.base.metadata"),
    type: "metadata",
  };

  if (customXMLMetadata) {
    if (!serverVersionedDocumentObjects.metadata) notActiveOnServer.push(metadataMessageItem);
    else if (customXMLMetadata.id !== serverVersionedDocumentObjects.metadata.id)
      // different id, was overwritten on server
      atDifferentVersion.push(metadataMessageItem);
  } else if (serverVersionedDocumentObjects.metadata) {
    // not present in client but present on server
    notPresent.push(metadataMessageItem);
  }

  const notValid: SyncRestoreObjectInfo[] = contentControlsWithFirstLevelChild.reduce((acc, cc) => {
    if (!(docCcIdMap.has(cc.id.toString()) || serverCcIdMap.has(cc.id.toString())))
      acc.push({
        name: cc.title,
        type: "",
        contentControlId: cc.id,
      });
    return acc;
  }, []);

  return {
    unSyncObjects: {
      notPresentInDoc: notPresent,
      atDifferentVersion: atDifferentVersion,
      notActiveOnServer: notActiveOnServer,
      notTagetik: notValid,
      tot: atDifferentVersion.length + notPresent.length + notActiveOnServer.length + notValid.length,
    },
  };
};

const CellRenderer = (info: SyncRestoreObjectInfo) => (
  <li>
    <span
      onClick={info.contentControlId ? () => moveCursorToContentControlId(parseInt(info.contentControlId)) : undefined}
      style={info.contentControlId ? { textDecoration: "underline", cursor: "pointer" } : undefined}
    >
      {info.name} {info.type && `(${info.type})`}
    </span>
  </li>
);

export const SynchRestorePanel: React.FunctionComponent<SyncRestoreProps> = (props) => {
  const [state, setState] = useState<SynchRestoreState>({
    unSyncObjects: undefined,
  });
  const { unSyncObjects } = state;
  const { documentCoObjects, documentId } = props;

  const updateVersionedObjectDiffInState = async () => {
    setState({ ...state, unSyncObjects: undefined }); // remove data rendered on the panel
    LoadingManager.create(SYNCH_RESTORE);
    const serverVersionedDocumentObjects = await getServerVersionedDocumentObjects(documentId); // get server coObjects and metadata(s)
    const customXMLMetadata = await getDocumentMetadataFromCustomXML(); // get metadata from custom XML, mapped to a DocumentPartMetadata
    const contentControlsWithFirstLevelChild = await getContentControlsWithFirstLevelChild();
    const newState = parseVersionedObjectDiffToState(
      serverVersionedDocumentObjects,
      documentCoObjects,
      contentControlsWithFirstLevelChild,
      customXMLMetadata
    ); // get new data to render on the panel
    setState(newState);
    LoadingManager.remove(SYNCH_RESTORE);
  };

  useEffect(() => {
    updateVersionedObjectDiffInState();
  }, [documentCoObjects]);

  const syncRemoteObjects = async () => {
    setState({ ...state, unSyncObjects: undefined });
    LoadingManager.create(SYNCH_RESTORE);
    const docPartMetadata = await getDocumentMetadataFromCustomXML();
    const syncRequest = buildSyncRequest(documentId, documentCoObjects, docPartMetadata);

    const message = await syncVersionedDocumentObjects(syncRequest);

    if (message && message.length > 0) {
      NotificationManager.addNotification({
        title: formatMessage("cof.synch.restoresynchronization"),
        message: message,
        key: "SYNC_RESTORE",
        type: "warning",
        closable: true,
        removeOnClose: true,
        isTemporary: true,
      });
    } else {
      NotificationManager.addNotification({
        title: formatMessage("cof.synch.restoresynchronization"),
        message: formatMessage("cof.synch.message.success"),
        key: "SYNC_RESTORE",
        type: "success",
        closable: true,
        removeOnClose: true,
        isTemporary: true,
      });
    }
    // UPDATE
    await updateVersionedObjectDiffInState();
  };

  return (
    <>
      <HeaderPanel applyPadding>{formatMessage("cof.synch.info")}</HeaderPanel>
      {unSyncObjects && (
        <div style={{ padding: "10px" }}>
          {unSyncObjects.tot == 0 ? (
            <div>{formatMessage("cof.sync.info")}</div>
          ) : (
            <>
              <div>{formatMessage("cof.sync.error")}</div>
              <br />
              {unSyncObjects.atDifferentVersion.length > 0 && (
                <>
                  <div>
                    <b>{formatMessage("cof.sync.difference")}</b>:
                  </div>
                  <List items={unSyncObjects.atDifferentVersion} onRenderCell={CellRenderer}></List>
                  <br />
                </>
              )}
              {unSyncObjects.notPresentInDoc.length > 0 && (
                <>
                  <div>
                    <b>{formatMessage("cof.sync.notpresent")}</b>:
                  </div>
                  <List items={unSyncObjects.notPresentInDoc} onRenderCell={CellRenderer}></List>
                  <br />
                </>
              )}
              {unSyncObjects.notActiveOnServer.length > 0 && (
                <>
                  <div>
                    <b>{formatMessage("cof.sync.notactive")}</b>:
                  </div>
                  <List items={unSyncObjects.notActiveOnServer} onRenderCell={CellRenderer}></List>
                  <br />
                </>
              )}
              {unSyncObjects.notTagetik.length > 0 && (
                <>
                  <div>
                    <b>{formatMessage("cof.sync.nottagetik")}</b>:
                  </div>
                  <List items={unSyncObjects.notTagetik} onRenderCell={CellRenderer}></List>
                  <br />
                </>
              )}
            </>
          )}

          <FooterPanel>
            <div className="ms-Grid-col ms-xs4 ms-sm4 ms-md4 ms-lg4" style={{ float: "right" }}>
              <PrimaryButton
                text={formatMessage("cof.base.synchronize")}
                style={{ float: "right" }}
                onClick={() => {
                  syncRemoteObjects();
                }}
                disabled={unSyncObjects.tot == 0}
              />
            </div>
          </FooterPanel>
        </div>
      )}
    </>
  );
};

function buildSyncRequest(
  documentId: string,
  coObjects: CoObjectItem[],
  docPartMetadata: DocumentPartMetadata | null
): SyncVersionedDocumentObjectRequest {
  const documentObjects: CoObject[] = coObjects.map((item) => {
    const coObject: CoObject = Object.create(null);
    coObject.id = item.id;
    coObject.type = CoObjectTypeEnum[item.info.objectType];
    coObject.name = item.info.objectName;
    coObject.desc = item.info.info;
    coObject.documentId = item.documentId;
    coObject.contentControlId = item.contentControlId;
    return coObject;
  });
  return {
    coObjects: documentObjects,
    documentPartMetadataId: docPartMetadata ? docPartMetadata.id : null,
    documentPartId: documentId,
  };
}
